import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, applyFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, ColoredBarChart, ChartColors } from '@/components/ReportCharts'

interface Row {
  id: number
  agent_name: string
  credit_type: string
  credit_amount: number
  expires_at: string
  days_to_expiry: number
  bucket: string
  event_name: string | null
}

interface ExpiredRow {
  id: number
  agent_name: string
  credit_type: string
  credit_amount: number
  expired_on: string
  event_name: string | null
}

interface Bucket { label: string; count: number; amount: number }
interface PerAgent { agent_id: number; agent_name: string | null; count: number; amount: number }

interface Props extends PageProps {
  buckets: Bucket[]
  per_agent: PerAgent[]
  rows: Row[]
  expired_rows: ExpiredRow[]
  date_range: DateRange
  all_agents: { id: number; name: string }[]
  filters: { agent_id: number | null }
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/pending_equity_aging'

export default function PendingEquityAging({ buckets, per_agent, rows, expired_rows, date_range, all_agents, filters, sort }: Props) {
  const filterState = { agent_id: filters.agent_id ?? '' }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  const detailColumns: Column<Row>[] = [
    { key: 'agent_name', header: 'Agent', sortable: true },
    { key: 'credit_type', header: 'Type', sortable: true },
    { key: 'credit_amount', header: 'Amount', sortable: true,
      render: (r) => r.credit_amount.toLocaleString() },
    { key: 'days_to_expiry', header: 'Days to Expiry', sortable: true, className: 'text-center' },
    { key: 'bucket', header: 'Bucket', sortable: true },
    { key: 'expires_at', header: 'Expires', sortable: true,
      render: (r) => new Date(r.expires_at).toLocaleDateString() },
    { key: 'event_name', header: 'Event', sortable: true,
      render: (r) => r.event_name || '—' }
  ]

  const agentColumns: Column<PerAgent>[] = [
    { key: 'agent_name', header: 'Agent', sortable: true,
      render: (r) => r.agent_name || 'Unknown' },
    { key: 'count', header: 'Pending Count', sortable: true, className: 'text-center' },
    { key: 'amount', header: 'Pending Amount', sortable: true,
      render: (r) => r.amount.toLocaleString() }
  ]

  const expiredColumns: Column<ExpiredRow>[] = [
    { key: 'agent_name', header: 'Agent', sortable: true },
    { key: 'credit_type', header: 'Type', sortable: true },
    { key: 'credit_amount', header: 'Amount', sortable: true,
      render: (r) => r.credit_amount.toLocaleString() },
    { key: 'expired_on', header: 'Expired On', sortable: true,
      render: (r) => (
        <span className="text-red-600 font-medium">
          {new Date(r.expired_on).toLocaleDateString()}
        </span>
      ) },
    { key: 'event_name', header: 'Event', sortable: true,
      render: (r) => r.event_name || '—' }
  ]

  return (
    <AppLayout>
      <Head title="Pending Equity Credit Aging" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Pending Equity Credit Aging</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Pending equity credits bucketed by days remaining until expiry.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Agent"
            value={filters.agent_id ?? ''}
            options={all_agents.map(a => ({ value: a.id, label: a.name }))}
            onChange={(v) => applyFilter(PATH, { ...filterState, preset: date_range.preset, start_date: date_range.start_date, end_date: date_range.end_date }, 'agent_id', v)}
          />
        </ReportControlBar>
        <p className="text-xs text-chestnut-charcoal/60 -mt-3">Date range filters which credits to include by accrual date (created_at). Aging buckets are computed from time until expiry.</p>

        <ChartCard
          title="Pending credits by aging bucket"
          subtitle="Number of pending credits in each days-to-expiry bucket. Shorter buckets are closer to expiring."
          empty={buckets.every(b => b.count === 0)}
          emptyMessage="No pending equity credits."
        >
          <ColoredBarChart
            data={buckets.map((b, i) => ({
              label: b.label,
              value: b.count,
              color: i === 0 ? ChartColors.RED
                : i === 1 ? ChartColors.AMBER
                : i === 2 ? ChartColors.BLUE
                : ChartColors.FOREST
            }))}
            tooltipLabel="Pending"
          />
        </ChartCard>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {buckets.map(b => (
            <div key={b.label} className="bg-white rounded-xl border border-chestnut-gray p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-chestnut-charcoal/60">{b.label}</p>
              <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{b.count}</p>
              <p className="text-sm text-chestnut-charcoal/60">{b.amount.toLocaleString()} credits</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <h2 className="text-lg font-semibold text-chestnut-charcoal mb-4">Per Agent</h2>
          <DataTable
            data={per_agent}
            columns={agentColumns}
            searchPlaceholder="Search agents..."
            searchKeys={['agent_name']}
            pageSize={15}
            emptyMessage="No pending equity credits."
            getRowKey={(r) => r.agent_id}
            defaultSortKey="amount"
            defaultSortDirection="desc"
          />
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <h2 className="text-lg font-semibold text-chestnut-charcoal mb-4">All Pending Credits</h2>
          <DataTable
            data={rows}
            columns={detailColumns}
            searchPlaceholder="Search..."
            searchKeys={['agent_name', 'event_name']}
            pageSize={20}
            emptyMessage="No pending equity credits."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'days_to_expiry', direction: (sort.dir as 'asc' | 'desc') ?? 'asc' }}
            onSortChange={onSortChange}
          />
        </div>

        {/* Expired / forfeited section — admin-visible, clearly separated */}
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-red-700">Expired Credits (Forfeited)</h2>
            <p className="text-sm text-chestnut-charcoal/60 mt-0.5">
              Equity credits that were forfeited because the agent did not sign their contract before the deadline.
              Each row shows the date the credit expired. Not visible to agents.
            </p>
          </div>
          <DataTable
            data={expired_rows}
            columns={expiredColumns}
            searchPlaceholder="Search..."
            searchKeys={['agent_name', 'event_name']}
            pageSize={20}
            emptyMessage="No expired equity credits in this date range."
            getRowKey={(r) => r.id}
            defaultSortKey="expired_on"
            defaultSortDirection="desc"
          />
        </div>
      </div>
    </AppLayout>
  )
}
